import { useTranslation } from 'react-i18next';

import type { QuestionScoreRow } from './types';

import { Badge } from '~/components/ui/badge';

interface EligibilityResultsProps {
  questions: QuestionScoreRow[];
  eligibilityPassed: string | null;
}

function getVerdictVariant(
  verdict: string | null
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (verdict) {
    case 'pass':
      return 'default';
    case 'fail':
      return 'destructive';
    default:
      return 'secondary';
  }
}

export function EligibilityResults({
  questions,
  eligibilityPassed
}: EligibilityResultsProps): React.ReactNode {
  const { t } = useTranslation();

  if (questions.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('scoring.eligibility')}</h2>
        {eligibilityPassed && (
          <Badge
            variant={
              eligibilityPassed === 'pass'
                ? 'default'
                : eligibilityPassed === 'fail'
                  ? 'destructive'
                  : 'secondary'
            }
          >
            {t(`scoring.verdicts.${eligibilityPassed}` as 'scoring.verdicts.pass')}
          </Badge>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {questions.map((q) => (
          <div
            key={q.id}
            className="rounded-lg border border-border bg-card p-4 shadow-sm border-l-4 border-l-[var(--color-section-blue)]"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium">{q.questionText}</p>
              <Badge variant={getVerdictVariant(q.verdict)} className="shrink-0">
                {t(`scoring.verdicts.${q.verdict ?? 'unsure'}` as 'scoring.verdicts.pass')}
              </Badge>
            </div>
            <div className="mt-2 flex flex-col gap-1">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{t('scoring.reasoning')}:</span> {q.reasoning}
              </p>
              <p className="text-sm italic text-muted-foreground">&ldquo;{q.evidence}&rdquo;</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
